import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { createNewTestComponent, getTasks, getTestComponent, updateTestComponent } from '../../../apiClients/backendApi';
import useTitle from '../../../systems/TitleManager/useTitle';
import { useLevels } from '@dyl/systems/LevelsManager';
import { without } from 'lodash';
import { exportKeys } from "@dyl/utils/exportKeys";
import { useAuth } from '@dyl/systems/UserManager';

/**
 * @template T
 * @typedef {[T, (next: T | (prev: T) => T) => void]} UseStateReturn
 */


/**
 * @typedef TaskOption
 * @property {string} task_id
 * @property {boolean} is_published
 * @property {string} task_id_name
 * @property {'speaking' | 'reading_writing' | 'listening'} skill_id
 * @property {string} task_type_id
 * @property {string} description
 */


export default function useLogic({ isNew = false }) {
    const navigate = useNavigate();
    const { user } = useAuth()
    /** @type {{ level?: string, skill?: string, componentId?: string }} */
    const params = useParams();
    const { enqueueSnackbar } = useSnackbar();

    // If not new (edit mode), is initially loading, as component must be loaded.
    const [isLoading, setLoading] = useState(!isNew);

    const { levels, globalLevel } = useLevels();

    /** @type {UseStateReturn<TaskOption[]} */
    const [taskOptions, setTaskOptions] = useState([]);

    const [name, setName] = useState('');
    const [level, setLevel] = useState(params.level ?? globalLevel.level_id);
    const [skill, setSkill] = useState(params.skill ?? '');
    /** @type {UseStateReturn<TaskOption[]} */
    const [selectedTaskIds, setSelectedTaskIds] = useState([]);
    const [qpvReference, setQpvReference] = useState(undefined);
    const [disableQpvRef, setDisableQpvRef] = useState(false);

    const selectedTasks = useMemo(() => {
        return selectedTaskIds.map(id => taskOptions.find(t => t.task_id === id));
    }, [taskOptions, selectedTaskIds]);

    const hasContent = selectedTasks.length > 0;

    const [isSaving, setSaving] = useState(false);

    const saveComponent = async () => {
        setSaving(true);

        let outputComponentId = params.componentId;

        try {
            let componentId = params.componentId;
            const taskIds = selectedTasks.map(t => t.task_id);

            if (isNew) {
                const res = await createNewTestComponent(level, skill, name, qpvReference);

                componentId = res.test_id;
                outputComponentId = componentId;
            }

            if (!componentId) {
                throw new Error('No component ID');
            }
            else if (!hasContent) {
                throw new Error('No content');
            }

            await updateTestComponent(
                level,
                skill,
                componentId,
                taskIds,
                name,
                qpvReference
            );
        }
        catch (error) {
            enqueueSnackbar(
                'Failed to create component',
                { variant: 'error' }
            );

            console.error(error);

            return;
        }
        finally {
            setSaving(false);
        }

        enqueueSnackbar(
            'Successfully updated component',
            { variant: 'success' }
        );

        // Once created, go to the edit screen.
        if (isNew) {
            navigate(`/${globalLevel.label}/components/${skill}/${outputComponentId}`);
        }
    };

    const title = (
        isLoading ? 'Loading...'
        : isNew ? 'New Component'
        : name || '<NO NAME>'
    );

    useTitle({ breadcrumbs: ['Components', title] });

    // On skill/level change
    useEffect(() => {
        //TODO: show loading overlay

        // For single-skill, download skill-specific content
        (async () => {
            const tasks = await getTasks(globalLevel.level_id);
            const filtered = tasks.filter(task => task.skill_id === skill);

            setTaskOptions(filtered);
        })();
    }, [level, skill]);

    const _shouldDownloadComponent = (
        !isNew
        && taskOptions.length > 0
    );

    // For edit mode, download component once appropriate data ready
    useEffect(() => {
        if (_shouldDownloadComponent) {
            (async () => {
                const component = await getTestComponent(level, skill, params.componentId);

                setName(component.test_name);
                setQpvReference(component.qpv_reference ?? undefined);
                setSelectedTaskIds(component.tasks.map(t => t.task_id));
                setDisableQpvRef(component.disable_qpv_ref || false);

                setLoading(false);
            })();
        }
    }, [_shouldDownloadComponent]);

    const onNameChange = useCallback(e => setName(e.target.value), []);
    const onLevelChange = useCallback(e => setLevel(e.target.value), []);
    const onSkillChange = useCallback(e => setSkill(e.target.value), []);
    const onQpvReferenceChange = useCallback(e => {
        let qpvReferenceValue = e.target.value;
        qpvReferenceValue = qpvReferenceValue.trim() === '' ? undefined : qpvReferenceValue
        setQpvReference(qpvReferenceValue)
    }, []);
    const onDisableQpvRef = useCallback(e => {
        setDisableQpvRef(e.target.value);
    }, [setDisableQpvRef]);

    const removeTask = useCallback(id => {
        setSelectedTaskIds(prev => without(prev, id));
    }, []);

    // Levels are set globally from the side-nav bar.
    const allowLevelChange = false; // isNew && !hasContent && levelsStatus === 'complete';
    const allowSkillChange = isNew && !hasContent;
    const allowContentEditing = !!level && !!skill;
    const allowAddContent = taskOptions.length > 0;
    const allowSaving = !!name && !isSaving && hasContent;

    const exportComponentKeys = useCallback(exportKeys(globalLevel, name), [globalLevel, name]);

    return {
        isNew,
        isLoading,

        levels,
        level,
        onLevelChange,
        allowLevelChange,

        skill,
        onSkillChange,
        allowSkillChange,

        name,
        onNameChange,

        allowContentEditing,
        allowAddContent,
        taskOptions,

        selectedTasks,
        selectedTaskIds,
        setSelectedTaskIds,
        removeTask,

        allowSaving,
        saveComponent,
        isSaving,

        qpvReference,
        onQpvReferenceChange,

        disableQpvRef,
        onDisableQpvRef,

        exportComponentKeys
    };
}